import React, { type ComponentPropsWithoutRef, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import Lozenge from '@atlaskit/lozenge';
import { Flex } from '@atlaskit/primitives';
import { Section, HeadingItem } from '@atlaskit/side-navigation';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { matchPartialUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import type { nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentConfigurationRef$key } from '@atlassian/jira-relay/src/__generated__/nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentConfigurationRef.graphql';
import type { nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentFeaturesRef$key } from '@atlassian/jira-relay/src/__generated__/nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentFeaturesRef.graphql';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import { FeedbackMenuItem } from './feedback-menu-item/index.tsx';
import messages from './messages.tsx';
import { matchVirtualAgentPathname } from './utils.tsx';

export const VirtualAgentNestedNav: FC<{
	projectKey: string;
	overrides: ComponentPropsWithoutRef<typeof NestingMenuItem>['overrides'];
	shifter?: {
		groupTitle: string;
		context: string;
		weight: number;
	};
	virtualAgentFeaturesRef: nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentFeaturesRef$key;
	virtualAgentConfigurationRef: nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentConfigurationRef$key | null;
}> = ({
	projectKey,
	overrides,
	shifter,
	virtualAgentFeaturesRef,
	virtualAgentConfigurationRef,
}) => {
	const { formatMessage } = useIntl();

	const virtualAgentFeatures = useFragment(
		graphql`
			fragment nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentFeaturesRef on VirtualAgentFeatures
			@argumentDefinitions(
				checkForAiEnabledFF: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsm-va-is-require-ai-enabled.relayprovider"
				}
			) {
				isVirtualAgentAvailable
				isAiEnabledInAdminHub @include(if: $checkForAiEnabledFF)
			}
		`,
		virtualAgentFeaturesRef,
	);

	const virtualAgentConfiguration = useFragment(
		graphql`
			fragment nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentConfigurationRef on VirtualAgentConfiguration {
				__typename
			}
		`,
		virtualAgentConfigurationRef,
	);
	const isVirtualAgentConfigured =
		virtualAgentConfiguration?.__typename === 'VirtualAgentConfiguration';

	const [{ data: serviceDeskContext }] = useServiceDeskContext();
	const testModeUrl =
		serviceDeskContext && `${serviceDeskContext.portalUrl}/conversation?isTestConversation=true`;

	const isSiteEntitledToUseVa = fg('require_ai_enabled_for_jsm_virtual_agent')
		? virtualAgentFeatures?.isVirtualAgentAvailable && virtualAgentFeatures?.isAiEnabledInAdminHub
		: virtualAgentFeatures?.isVirtualAgentAvailable;

	return (
		<NestingMenuItem
			id="virtualagent"
			analytics={{
				itemId: 'virtualagent',
			}}
			title={formatMessage(messages.virtualServiceAgent)}
			href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/${
				isSiteEntitledToUseVa ? 'intents' : 'settings'
			}`}
			selectedOn={matchVirtualAgentPathname()}
			overrides={overrides}
			shifter={shifter}
			iconAfter={
				<Lozenge appearance="new">{formatMessage(messages.virtualAgentLozengeText)}</Lozenge>
			}
		>
			<Section>
				<HeadingItem>
					<Flex justifyContent="space-between">
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p>{formatMessage(messages.virtualServiceAgent)}</p>
						<Lozenge appearance="new">{formatMessage(messages.virtualAgentLozengeText)}</Lozenge>
					</Flex>
				</HeadingItem>
				{/* Only show settings when user is not premium or enterprise or does not have Ai enabled */}
				{isSiteEntitledToUseVa && (
					<>
						{fg('jsm_va_enable_performance_page') && (
							<MenuItem
								href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/performance`}
								selectedOn={matchPartialUrlPathname()}
							>
								{formatMessage(messages.performance)}
							</MenuItem>
						)}
						{fg('jsm_va_enable_ai_answers_page') && (
							<MenuItem
								href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/ai-answers`}
								selectedOn={matchPartialUrlPathname()}
							>
								{formatMessage(messages.aiAnswers)}
							</MenuItem>
						)}
						<MenuItem
							href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/intents`}
							selectedOn={matchPartialUrlPathname()}
						>
							{formatMessage(messages.intents)}
						</MenuItem>
						<MenuItem
							href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/standard-flows`}
							selectedOn={matchPartialUrlPathname()}
						>
							{formatMessage(messages.standardFlows)}
						</MenuItem>
						<MenuItem
							href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/conversations`}
							selectedOn={matchPartialUrlPathname()}
						>
							{formatMessage(messages.conversations)}
						</MenuItem>
					</>
				)}
				<MenuItem
					href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/settings`}
					selectedOn={matchPartialUrlPathname()}
				>
					{formatMessage(messages.settings)}
				</MenuItem>
				{isSiteEntitledToUseVa &&
					testModeUrl &&
					(fg('jsm_portal_test_mode_requires_va_configured') ? isVirtualAgentConfigured : true) && (
						<MenuItem
							href={testModeUrl}
							iconAfter={<ShortcutIcon spacing="spacious" label="" />}
							target="_blank"
						>
							{formatMessage(messages.test)}
						</MenuItem>
					)}
			</Section>
			{fg('jsm-virtual-agent-feedback-collector') && (
				<Section hasSeparator>
					<FeedbackMenuItem />
				</Section>
			)}
		</NestingMenuItem>
	);
};
